import React, { useState } from "react";
import { uploadToCloudinary } from "../services/cloudinaryService";

const BookForm = ({ onSubmit }) => {
    const [formData, setFormData] = useState({
        title: "",
        author: "",
        genre: "",
        bookType: "trade_paperback",
        description: "",
        language: "English",
        aboutAuthor: "",
        authorPhoto: "",
        authorSignature: "",
    });
    const [uploading, setUploading] = useState(false);

    const handleChange = (e) => {
        setFormData((prev) => ({
            ...prev,
            [e.target.name]: e.target.value,
        }));
    };

    const handlePhotoUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        try {
            setUploading(true);
            const response = await uploadToCloudinary(file);
            setFormData((prev) => ({
                ...prev,
                authorPhoto: response.url,
            }));
        } catch (error) {
            console.error("Error uploading author photograph:", error);
            alert("Failed to upload photo. " + error.message);
        } finally {
            setUploading(false);
        }
    };

    const handleSignatureUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        try {
            setUploading(true);
            const response = await uploadToCloudinary(file);
            setFormData((prev) => ({
                ...prev,
                authorSignature: response.url,
            }));
        } catch (error) {
            console.error("Error uploading author signature:", error);
            alert("Failed to upload signature. " + error.message);
        } finally {
            setUploading(false);
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        onSubmit(formData);
    };

    return (
        <form
            onSubmit={handleSubmit}
            className="bg-white p-6 rounded-xl shadow"
        >
            <h2 className="text-2xl font-bold mb-6 text-blue-900">
                Create New Book
            </h2>

            <div className="mb-4">
                <label className="block text-sm font-bold text-gray-700 mb-2">Book Title</label>
                <input
                    type="text"
                    name="title"
                    placeholder="Book Title"
                    className="w-full border p-3 rounded"
                    value={formData.title}
                    onChange={handleChange}
                    required
                />
            </div>

            <div className="mb-4">
                <label className="block text-sm font-bold text-gray-700 mb-2">Author Name</label>
                <input
                    type="text"
                    name="author"
                    placeholder="Author Name"
                    className="w-full border p-3 rounded"
                    value={formData.author}
                    onChange={handleChange}
                    required
                />
            </div>

            <div className="mb-4">
                <label className="block text-sm font-bold text-gray-700 mb-2">Genre</label>
                <input
                    type="text"
                    name="genre"
                    placeholder="Genre"
                    className="w-full border p-3 rounded"
                    value={formData.genre}
                    onChange={handleChange}
                    required
                />
            </div>

            <div className="mb-4">
                <label className="block text-sm font-bold text-gray-700 mb-2">Book Type (Page Size)</label>
                <select
                    name="bookType"
                    className="w-full border p-3 rounded bg-white text-gray-800 font-semibold"
                    value={formData.bookType}
                    onChange={handleChange}
                    required
                >
                    <option value="pocket_book">Pocket Book (4.25" × 6.87") — Small novels</option>
                    <option value="small_paperback">Small Paperback (5" × 8") — Fiction, poetry</option>
                    <option value="digest_size">Digest Size (5.5" × 8.5") — Most novels</option>
                    <option value="trade_paperback">Trade Paperback (6" × 9") — Business, non-fiction</option>
                    <option value="large_book">Large Book (7" × 10") — Manuals, workbooks</option>
                    <option value="childrens_book">Children's Book (8" × 10") — Illustrated books</option>
                    <option value="square_book">Square Book (8.5" × 8.5") — Cookbooks, kids books</option>
                    <option value="textbook">Textbook (8.5" × 11") — Educational</option>
                    <option value="a5">A5 (5.83" × 8.27") — Notebooks, journals</option>
                    <option value="a4">A4 (8.27" × 11.69") — Reports, materials</option>
                </select>
            </div>

            <div className="mb-4">
                <label className="block text-sm font-bold text-gray-700 mb-2">Book Description</label>
                <textarea
                    rows="3"
                    name="description"
                    placeholder="Book Description"
                    className="w-full border p-3 rounded"
                    value={formData.description}
                    onChange={handleChange}
                    required
                />
            </div>

            <div className="mb-4">
                <label className="block text-sm font-bold text-gray-700 mb-2">About the Author</label>
                <textarea
                    rows="3"
                    name="aboutAuthor"
                    placeholder="Brief author biography..."
                    className="w-full border p-3 rounded"
                    value={formData.aboutAuthor}
                    onChange={handleChange}
                />
            </div>

            <div className="mb-6">
                <label className="block text-sm font-bold text-gray-700 mb-2">Author Photograph</label>
                <div className="flex items-center gap-4 p-3 border rounded bg-gray-50">
                    {formData.authorPhoto ? (
                        <div className="relative w-16 h-16 rounded-full overflow-hidden border-2 border-red-500 shadow-sm flex-shrink-0">
                            <img
                                src={formData.authorPhoto}
                                alt="Author preview"
                                className="w-full h-full object-cover"
                            />
                            <button
                                type="button"
                                onClick={() => setFormData(prev => ({ ...prev, authorPhoto: "" }))}
                                className="absolute inset-0 bg-black/60 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center text-white text-[10px] font-bold"
                            >
                                Remove
                            </button>
                        </div>
                    ) : (
                        <div className="w-16 h-16 rounded-full bg-gray-200 border border-gray-300 flex items-center justify-center text-xl text-gray-500 flex-shrink-0">
                            👤
                        </div>
                    )}
                    <div className="flex-1">
                        <input
                            type="file"
                            accept="image/*"
                            onChange={handlePhotoUpload}
                            disabled={uploading}
                            className="hidden"
                            id="author-photo-input"
                        />
                        <label
                            htmlFor="author-photo-input"
                            className={`inline-block px-4 py-2 bg-white border border-gray-300 rounded text-xs font-semibold text-gray-700 cursor-pointer hover:bg-gray-100 transition-colors ${uploading ? "opacity-50 cursor-not-allowed" : ""}`}
                        >
                            {uploading ? "Uploading..." : formData.authorPhoto ? "Change Photo" : "Upload Photo"}
                        </label>
                        <p className="text-[10px] text-gray-500 mt-1">
                            PNG, JPG or JPEG (Max 5MB)
                        </p>
                    </div>
                </div>
            </div>

            <div className="mb-6">
                <label className="block text-sm font-bold text-gray-700 mb-2">Author Signature</label>
                <div className="flex items-center gap-4 p-3 border rounded bg-gray-50">
                    {formData.authorSignature ? (
                        <div className="relative w-24 h-12 border-2 border-red-500 bg-white rounded shadow-sm flex-shrink-0 flex items-center justify-center overflow-hidden">
                            <img
                                src={formData.authorSignature}
                                alt="Signature preview"
                                className="max-w-full max-h-full object-contain"
                            />
                            <button
                                type="button"
                                onClick={() => setFormData(prev => ({ ...prev, authorSignature: "" }))}
                                className="absolute inset-0 bg-black/60 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center text-white text-[10px] font-bold"
                            >
                                Remove
                            </button>
                        </div>
                    ) : (
                        <div className="w-24 h-12 rounded border border-gray-300 bg-gray-200 flex items-center justify-center text-xs text-gray-500 flex-shrink-0 font-mono select-none">
                            SIGNATURE
                        </div>
                    )}
                    <div className="flex-1">
                        <input
                            type="file"
                            accept="image/*"
                            onChange={handleSignatureUpload}
                            disabled={uploading}
                            className="hidden"
                            id="author-signature-input"
                        />
                        <label
                            htmlFor="author-signature-input"
                            className={`inline-block px-4 py-2 bg-white border border-gray-300 rounded text-xs font-semibold text-gray-700 cursor-pointer hover:bg-gray-100 transition-colors ${uploading ? "opacity-50 cursor-not-allowed" : ""}`}
                        >
                            {uploading ? "Uploading..." : formData.authorSignature ? "Change Signature" : "Upload Signature"}
                        </label>
                        <p className="text-[10px] text-gray-500 mt-1">
                            PNG, JPG or JPEG (preferably transparent background)
                        </p>
                    </div>
                </div>
            </div>

            <button
                type="submit"
                disabled={uploading}
                className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-6 py-3 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
                Create Book
            </button>
        </form>
    );
};

export default BookForm;